import React from 'react';
import { GitBranch, ArrowRight, Check } from 'lucide-react';

const DecisionCard = ({ card, onOptionSelect, disabled = false, selectedOptionId = null }) => {
  const { title, description, options = [] } = card;
  const selectedOption = selectedOptionId ? options.find(opt => opt.id === selectedOptionId) : null;
  // Only collapse if an option is selected AND that option exists in the current card
  // This prevents empty cards when ID conflicts occur across different cards
  const isCollapsed = selectedOptionId !== null && !!selectedOption;

  return (
    <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl border border-indigo-100 dark:border-indigo-900/50 shadow-lg shadow-indigo-100/50 dark:shadow-none ring-1 ring-indigo-50 dark:ring-indigo-900/20">
      {/* Decorative background pattern */}
      <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-indigo-50/60 to-purple-50/40 dark:from-indigo-900/15 dark:to-purple-900/10 rounded-bl-full -mr-12 -mt-12 pointer-events-none" />
      
      {/* Header Section - icon left pattern */}
      <div className="relative flex items-start gap-6 p-4 pb-3">
        {/* Icon column */}
        <div className="flex-shrink-0">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-indigo-100 to-indigo-50 dark:from-indigo-900/50 dark:to-indigo-800/30 text-indigo-600 dark:text-indigo-400 shadow-sm ring-1 ring-indigo-200/50 dark:ring-indigo-700/30">
            <GitBranch size={22} strokeWidth={2.5} />
          </div>
        </div>
        
        {/* Title/description */}
        <div className="flex-1 min-w-0 pt-1">
          <h3 className="text-base font-bold text-gray-900 dark:text-gray-50 leading-tight">
            {title || 'Decision Needed'}
          </h3>
          {description && !isCollapsed && (
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              {description}
            </p>
          )}
          {isCollapsed && selectedOption && (
            <div className="mt-3 w-full">
              <div className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-indigo-50 to-indigo-100/50 dark:from-indigo-900/40 dark:to-indigo-800/30 border-2 border-indigo-200 dark:border-indigo-700 rounded-xl">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-indigo-500 dark:bg-indigo-600 flex items-center justify-center">
                  <Check size={16} className="text-white" strokeWidth={3} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wide mb-0.5">
                    Selected
                  </div>
                  <div className="text-base font-bold text-indigo-900 dark:text-indigo-100">
                    {selectedOption.label}
                  </div>
                  {selectedOption.description && (
                    <div className="text-sm text-indigo-700 dark:text-indigo-300 mt-1">
                      {selectedOption.description}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Options Grid - Collapsed when option is selected */}
      {!isCollapsed && (
        <div className="relative px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {options.map((option, idx) => {
            const isSelected = selectedOptionId === option.id;
            return (
              <button
                key={option.id || idx}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!disabled) {
                    onOptionSelect(option);
                  }
                }}
                disabled={disabled}
                className={`
                  group relative flex flex-col items-start p-4 h-full text-left rounded-xl border-2 transition-all duration-200
                  ${isSelected
                    ? 'bg-gradient-to-br from-indigo-100 to-indigo-50 dark:from-indigo-900/50 dark:to-indigo-800/30 border-indigo-400 dark:border-indigo-600 ring-2 ring-indigo-300 dark:ring-indigo-700 shadow-md'
                    : disabled 
                      ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700' 
                      : 'bg-indigo-50/30 dark:bg-indigo-900/10 border-indigo-100 dark:border-indigo-800/50 hover:bg-indigo-100/50 dark:hover:bg-indigo-900/20 hover:border-indigo-300 dark:hover:border-indigo-700 hover:shadow-md active:scale-[0.98] cursor-pointer'
                  }
                `}
              >
                <div className="flex items-center justify-between w-full mb-2">
                  <span className={`
                    inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold transition-colors
                    ${isSelected
                      ? 'bg-indigo-500 dark:bg-indigo-600 text-white'
                      : disabled
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        : 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 group-hover:bg-indigo-200 dark:group-hover:bg-indigo-800'
                    }
                  `}>
                    {isSelected ? <Check size={16} strokeWidth={3} /> : String.fromCharCode(65 + idx)}
                  </span>
                  {isSelected ? (
                    <Check size={18} className="text-indigo-600 dark:text-indigo-400" strokeWidth={2.5} />
                  ) : !disabled && (
                    <ArrowRight size={16} className="text-indigo-300 group-hover:text-indigo-500 transition-all opacity-0 group-hover:opacity-100 transform translate-x-[-4px] group-hover:translate-x-0" />
                  )}
                </div>
                
                <div className={`font-semibold text-[15px] mb-1 ${isSelected ? 'text-indigo-900 dark:text-indigo-100' : disabled ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                  {option.label}
                </div>
                
                {option.description && (
                  <div className={`text-xs leading-relaxed ${isSelected ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-500 dark:text-gray-400'}`}>
                    {option.description}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DecisionCard;
